import { Roles } from '~/data/user';
import { Roles as UserRoles } from '~/data/user/role';

const isRole = (expectedRole: Roles) => (userRoles: Roles[] | string[]) =>
  userRoles.includes(expectedRole);

export const hasOneOfRoles = (
  roles: Roles[] | string[],
  expectedRoles: Roles[],
) => expectedRoles.some((role) => roles.includes(role));

export const isRoleDepManager = isRole(Roles.DEPUTY_MANAGER);
export const isRoleAltManager = isRole(Roles.ALT_MANAGER);
export const isRoleManager = (userRoles: Roles[] | string[]) =>
  isRole(Roles.MANAGER)(userRoles) || isRoleAltManager(userRoles);
export const isRoleHRAdminFull = isRole(Roles.HR_ADMIN);
export const isRoleHRAdminLimited = isRole(Roles.HR_ADMIN_LIMITED);
export const isRoleHRAdmin = (userRoles: Roles[] | string[]) =>
  isRoleHRAdminFull(userRoles) || isRoleHRAdminLimited(userRoles);
export const isRoleDevHR = isRole(Roles.HR_DEV);
export const isRolePeopHR = isRole(Roles.HR_PEOP);
export const isRolePeopDevHR = isRole(Roles.HR_PEOP_DEV);
export const isRoleStaffHR = isRole(Roles.HR_STAFF);
export const isRoleHR = (userRoles: Roles[] | string[]) =>
  isRoleStaffHR(userRoles) ||
  isRoleDevHR(userRoles) ||
  isRoleHRAdmin(userRoles);
export const isRoleITAdmin = isRole(Roles.IT_ADMIN);
export const isRoleRecruiter = isRole(Roles.RECRUITER);
export const isRoleHRSalary = isRole(Roles.HR_SALARY);

export enum RoleGroups {
  EMPLOYEE = 'EMPLOYEE',
  STRAIGHT_MANAGER = 'STRAIGHT_MANAGER',
  RESPONSIBLE_MANAGER = 'RESPONSIBLE_MANAGER',
  HR = 'HR',
}

type RoleGroupTranslationType = {
  [key in RoleGroups]: string;
};

export const roleGroupTranslationKeys: RoleGroupTranslationType = {
  [RoleGroups.EMPLOYEE]: 'laEmployee',
  [RoleGroups.RESPONSIBLE_MANAGER]: 'otherManagers',
  [RoleGroups.STRAIGHT_MANAGER]: 'laManager',
  [RoleGroups.HR]: 'laHR',
};

export const getRoleGroupTranslationKey = (roleGroup: RoleGroups): string => {
  return roleGroupTranslationKeys[roleGroup];
};

export const getRoleGroupTranslationKeys = (
  roleGroup: RoleGroups[],
): string[] => {
  return roleGroup.map((group) => getRoleGroupTranslationKey(group));
};

export const isEmployee = (roles: string[]): boolean =>
  roles.some((role) => role === 'EMPLOYEE');

export const isHR = (roles: string[]): boolean =>
  roles.some((role) => role.startsWith('HR_') && role !== 'HR_SALARY');

export const isResponsibleManager = (roles: string[]): boolean =>
  roles.some((role) => role.endsWith('_MANAGER'));

export const isStraightManager = (roles: string[]): boolean =>
  roles.some((role) => role === 'MANAGER');

export const getRoleGroups = (roles: string[]): RoleGroups[] => [
  // Make sure that everybody gets employee group
  // Regardless he is an employee only or not
  RoleGroups.EMPLOYEE,
  ...(isHR(roles) && !isRoleHRSalary(roles) && !isRoleDevHR(roles)
    ? [RoleGroups.HR]
    : []),
  ...(isResponsibleManager(roles) ? [RoleGroups.RESPONSIBLE_MANAGER] : []),
  ...(isStraightManager(roles) ? [RoleGroups.STRAIGHT_MANAGER] : []),
];

type GroupsByRole = {
  [key in UserRoles]?: RoleGroups[];
};

export const getGroupsByRole = (): GroupsByRole => {
  return Object.fromEntries(
    Object.values(UserRoles).map((role) => {
      return [role, getRoleGroups([role])];
    }),
  );
};

type RolesByGroup = {
  [key in RoleGroups]?: UserRoles[];
};

export const getRolesByGroup = (): RolesByGroup => {
  const groupRolePairs = Object.entries(getGroupsByRole())
    .map(([role, groups]): [RoleGroups, UserRoles][] => {
      return groups.map((group): [RoleGroups, UserRoles] => [
        group,
        role as UserRoles,
      ]);
    })
    .flat();

  return Object.fromEntries(
    Object.values(RoleGroups).map((group) => {
      const roles = groupRolePairs
        .filter(([g]) => g === group)
        .map(([_, r]) => {
          return r;
        });

      return [group, roles];
    }),
  );
};
